import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import React from 'react';
import { FilesTooltip } from './config';
import type { Asset } from './types';

interface DeleteConfirmModalProps {
    assets: Asset[];
    isDeleting: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}

const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({
    assets,
    isDeleting,
    onConfirm,
    onCancel
}) => {
    const isBulk = assets.length > 1;

    return (
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm"
            onClick={isDeleting ? undefined : onCancel}
        >
            <div
                className="bg-white rounded-xl shadow-lg w-full max-w-md p-6 relative"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={onCancel}
                    disabled={isDeleting}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
                    aria-label="Close"
                >
                    <XMarkIcon className="w-5 h-5" />
                </button>

                <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                        <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900">
                            Delete {isBulk ? `${assets.length} files` : 'file'}?
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                            This action cannot be undone. {isBulk ? 'These files' : 'This file'}{' '}
                            will be permanently removed.
                        </p>

                        {assets.length <= 5 ? (
                            <div className="mt-3 space-y-1">
                                {assets.map((asset) => (
                                    <FilesTooltip key={String(asset.assetId)} content={asset.name}>
                                        <p className="text-sm text-gray-700 truncate">
                                            {asset.name}
                                        </p>
                                    </FilesTooltip>
                                ))}
                            </div>
                        ) : (
                            <div className="mt-3 space-y-1">
                                {assets.slice(0, 3).map((asset) => (
                                    <FilesTooltip key={String(asset.assetId)} content={asset.name}>
                                        <p className="text-sm text-gray-700 truncate">
                                            {asset.name}
                                        </p>
                                    </FilesTooltip>
                                ))}
                                <p className="text-sm text-gray-500">
                                    and {assets.length - 3} more...
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex justify-end items-center gap-3 mt-6">
                    <button
                        onClick={onCancel}
                        disabled={isDeleting}
                        className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={isDeleting}
                        className="px-4 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                    >
                        {isDeleting ? (
                            <>
                                <div className="w-3.5 h-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                                Deleting...
                            </>
                        ) : (
                            `Delete ${isBulk ? `${assets.length} files` : 'file'}`
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export { DeleteConfirmModal };
